import type { AxiosResponse } from "axios";
import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export type SetupSortDirection = "ASC" | "DESC";

export interface NamedReference {
  id?: string | number;
  name?: string;
  code?: string;
  enumName?: string;
  description?: string;
}

export interface SetupListParams {
  page?: number;
  size?: number;
  sort?: SetupSortDirection;
  sortBy?: string;
}

export interface UnitBrandRecord {
  id: string | number;
  name?: string;
  description?: string;
  active?: boolean | number;
  [key: string]: unknown;
}

export interface GeneralSetupRecord {
  id: string | number;
  name?: string;
  code?: string;
  description?: string;
  active?: boolean | number;
  systemType?: NamedReference | null;
  [key: string]: unknown;
}

export interface GeneralSetupPayload {
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  systemTypeId?: string | number;
}

export interface UnitModelRecord {
  id: string | number;
  name?: string;
  code?: string;
  commCode?: string;
  description?: string;
  active?: boolean | number;
  make?: NamedReference | null;
  unitType?: NamedReference | null;
  [key: string]: unknown;
}

export interface ParameterGroupRecord {
  id: string | number;
  name?: string;
  code?: string;
  active?: boolean | number;
  [key: string]: unknown;
}

export interface PossibleValue {
  name?: string;
  value?: string;
  [key: string]: unknown;
}

export interface ParameterRecord {
  id: string | number;
  parameterGroup?: NamedReference | null;
  packetCode?: string;
  propertyNameComm?: string;
  propertyNameInternal?: string;
  description?: string;
  bytes?: string;
  type?: string;
  minValue?: string;
  maxValue?: string;
  multiplier?: string;
  units?: string;
  active?: boolean | number;
  make?: NamedReference | null;
  createdBy?: string;
  updatedBy?: string;
  possibleValues?: PossibleValue[];
  [key: string]: unknown;
}

export interface UnitBrandPayload {
  name: string;
  description?: string;
  active: boolean;
}

export interface UnitModelPayload {
  name: string;
  makeId: string | number;
  code: string;
  commCode: string;
  unitTypeId: string | number;
  description?: string;
  active: boolean;
}

export interface ParameterGroupPayload {
  name: string;
  code: string;
  active: boolean;
}

export interface ParameterPayload {
  parameterGroupId: string | number;
  packetCode: string;
  propertyNameComm: string;
  propertyNameInternal: string;
  description?: string;
  bytes?: string;
  type?: string;
  minValue?: string;
  maxValue?: string;
  multiplier?: string;
  units?: string;
  active: boolean;
  makeId: string | number;
  createdBy?: string;
  updatedBy?: string;
  possibleValues: PossibleValue[];
}

export interface StatusPayload {
  active: boolean;
}

export interface SetupPaginatedResponse<T> {
  unitMakes?: T[];
  unitModels?: T[];
  parameterGroups?: T[];
  properties?: T[];
  clientTypes?: T[];
  clientSources?: T[];
  positions?: T[];
  clientBusinessTypes?: T[];
  data?: T[];
  items?: T[];
  content?: T[];
  totalItems?: number;
  totalElements?: number;
  total?: number;
  totalPages?: number;
  currentPage?: number;
  page?: number;
}

export interface LookupResponse {
  response?: NamedReference[];
}

const replaceId = (url: string, id: string | number) => url.replace("{id}", String(id));

const objectToFormData = (payload: Record<string, unknown>) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  return formData;
};

const getMultipartLanguageHeaders = (language?: string) => {
  const config = getLanguageHeaders(language);

  return {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "multipart/form-data",
    },
  };
};

export const getUnitBrandsList = (params: SetupListParams = {}, language?: string) =>
  ApiService.get<{ response: SetupPaginatedResponse<UnitBrandRecord> }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_BRAND.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    }
  );

type GeneralSetupEndpoint = {
  ADD: string;
  UPDATE: string;
  DELETE: string;
  LISTING: string;
  COLUMN: string;
};

const getGeneralSetupList = (
  endpoint: GeneralSetupEndpoint,
  params: SetupListParams,
  language?: string
) =>
  ApiService.get<{ response: SetupPaginatedResponse<GeneralSetupRecord> }>(endpoint.LISTING, {
    params,
    ...getLanguageHeaders(language),
  });

const createGeneralSetup = (
  endpoint: GeneralSetupEndpoint,
  payload: GeneralSetupPayload,
  language?: string
) => ApiService.post(endpoint.ADD, payload, getLanguageHeaders(language));

const updateGeneralSetup = (
  endpoint: GeneralSetupEndpoint,
  id: string | number,
  payload: Partial<GeneralSetupPayload>,
  language?: string
) =>
  ApiService.patch(
    replaceId(endpoint.UPDATE, id),
    { id, ...payload },
    getLanguageHeaders(language)
  );

const deleteGeneralSetup = (
  endpoint: GeneralSetupEndpoint,
  id: string | number,
  language?: string
) => ApiService.delete(replaceId(endpoint.DELETE, id), getLanguageHeaders(language));

const getGeneralSetupColumns = (endpoint: GeneralSetupEndpoint, language?: string) =>
  ApiService.get(endpoint.COLUMN, getLanguageHeaders(language));

const customerTypeEndpoint = ServicesUrlEndpoints.ADMINISTRATION.SETUP.CUSTOMER_TYPE;
const customerSourceEndpoint = ServicesUrlEndpoints.ADMINISTRATION.SETUP.CUSTOMER_SOURCE;
const corporationPositionEndpoint = ServicesUrlEndpoints.ADMINISTRATION.SETUP.CORPORATION_POSITION;
const businessActivitiesEndpoint = ServicesUrlEndpoints.ADMINISTRATION.SETUP.BUSINESS_ACTIVITIES;

export const getCustomerTypesList = (params: SetupListParams = {}, language?: string) =>
  getGeneralSetupList(customerTypeEndpoint, params, language);
export const createCustomerType = (payload: GeneralSetupPayload, language?: string) =>
  createGeneralSetup(customerTypeEndpoint, payload, language);
export const updateCustomerType = (
  id: string | number,
  payload: Partial<GeneralSetupPayload>,
  language?: string
) => updateGeneralSetup(customerTypeEndpoint, id, payload, language);
export const deleteCustomerType = (id: string | number, language?: string) =>
  deleteGeneralSetup(customerTypeEndpoint, id, language);
export const getCustomerTypeColumns = (language?: string) =>
  getGeneralSetupColumns(customerTypeEndpoint, language);

export const getCustomerSourcesList = (params: SetupListParams = {}, language?: string) =>
  getGeneralSetupList(customerSourceEndpoint, params, language);
export const createCustomerSource = (payload: GeneralSetupPayload, language?: string) =>
  createGeneralSetup(customerSourceEndpoint, payload, language);
export const updateCustomerSource = (
  id: string | number,
  payload: Partial<GeneralSetupPayload>,
  language?: string
) => updateGeneralSetup(customerSourceEndpoint, id, payload, language);
export const deleteCustomerSource = (id: string | number, language?: string) =>
  deleteGeneralSetup(customerSourceEndpoint, id, language);
export const getCustomerSourceColumns = (language?: string) =>
  getGeneralSetupColumns(customerSourceEndpoint, language);

export const getCorporationPositionsList = (params: SetupListParams = {}, language?: string) =>
  getGeneralSetupList(corporationPositionEndpoint, params, language);
export const createCorporationPosition = (payload: GeneralSetupPayload, language?: string) =>
  createGeneralSetup(corporationPositionEndpoint, payload, language);
export const updateCorporationPosition = (
  id: string | number,
  payload: Partial<GeneralSetupPayload>,
  language?: string
) => updateGeneralSetup(corporationPositionEndpoint, id, payload, language);
export const deleteCorporationPosition = (id: string | number, language?: string) =>
  deleteGeneralSetup(corporationPositionEndpoint, id, language);
export const getCorporationPositionColumns = (language?: string) =>
  getGeneralSetupColumns(corporationPositionEndpoint, language);

export const getBusinessActivitiesList = (params: SetupListParams = {}, language?: string) =>
  getGeneralSetupList(businessActivitiesEndpoint, params, language);
export const createBusinessActivity = (payload: GeneralSetupPayload, language?: string) =>
  createGeneralSetup(businessActivitiesEndpoint, payload, language);
export const updateBusinessActivity = (
  id: string | number,
  payload: Partial<GeneralSetupPayload>,
  language?: string
) => updateGeneralSetup(businessActivitiesEndpoint, id, payload, language);
export const deleteBusinessActivity = (id: string | number, language?: string) =>
  deleteGeneralSetup(businessActivitiesEndpoint, id, language);
export const getBusinessActivitiesColumns = (language?: string) =>
  getGeneralSetupColumns(businessActivitiesEndpoint, language);

export const getSystemTypes = (language?: string) =>
  ApiService.get(
    ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.SYSTEM_TYPE,
    getLanguageHeaders(language)
  );

export const createUnitBrand = (payload: UnitBrandPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_BRAND.ADD,
    payload,
    getLanguageHeaders(language)
  );

export const updateUnitBrand = (
  id: string | number,
  payload: Partial<UnitBrandPayload>,
  language?: string
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_BRAND.UPDATE, id),
    objectToFormData({ id, ...payload }),
    getMultipartLanguageHeaders(language)
  );

export const deleteUnitBrand = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_BRAND.DELETE, id),
    getLanguageHeaders(language)
  );

export const getUnitBrandColumns = (language?: string) =>
  ApiService.get(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_BRAND.COLUMN,
    getLanguageHeaders(language)
  );

export const getUnitBrandLookup = (params: Record<string, unknown> = {}, language?: string) =>
  ApiService.get<LookupResponse>(ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.UNIT_BRAND, {
    params,
    ...getLanguageHeaders(language),
  });

export const getUnitModelLookupByMake = (
  makeId: string | number,
  params: Record<string, unknown> = {},
  language?: string
) =>
  ApiService.get<LookupResponse>(ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.UNIT_MODEL_LOOKUP, {
    params: { ...params, makeId },
    ...getLanguageHeaders(language),
  });

export const getUnitModelsList = (params: SetupListParams = {}, language?: string) =>
  ApiService.get<{ response: SetupPaginatedResponse<UnitModelRecord> }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_MODELS.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    }
  );

export const createUnitModel = (payload: UnitModelPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_MODELS.ADD,
    payload,
    getLanguageHeaders(language)
  );

export const updateUnitModel = (
  id: string | number,
  payload: Partial<UnitModelPayload>,
  language?: string
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_MODELS.UPDATE, id),
    payload,
    getLanguageHeaders(language)
  );

export const updateUnitModelStatus = (id: string | number, payload: StatusPayload) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_MODELS.UPDATE, id),
    payload
  );

export const deleteUnitModel = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_MODELS.DELETE, id),
    getLanguageHeaders(language)
  );

export const getUnitModelColumns = (language?: string) =>
  ApiService.get(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.UNIT_MODELS.COLUMN,
    getLanguageHeaders(language)
  );

export const getUnitTypeLookup = (language?: string) =>
  ApiService.get<LookupResponse>(
    ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.UNIT_TYPE,
    getLanguageHeaders(language)
  );

export const getParameterGroupsList = (params: SetupListParams = {}, language?: string) =>
  ApiService.get<{ response: SetupPaginatedResponse<ParameterGroupRecord> }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETER_GROUP.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    }
  );

export const createParameterGroup = (payload: ParameterGroupPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETER_GROUP.ADD,
    objectToFormData({ ...payload }),
    getMultipartLanguageHeaders(language)
  );

export const updateParameterGroup = (
  id: string | number,
  payload: Partial<ParameterGroupPayload>,
  language?: string
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETER_GROUP.UPDATE, id),
    objectToFormData({ id, ...payload }),
    getMultipartLanguageHeaders(language)
  );

export const deleteParameterGroup = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETER_GROUP.DELETE, id),
    getLanguageHeaders(language)
  );

export const getParameterGroupColumns = (language?: string) =>
  ApiService.get(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETER_GROUP.COLUMN,
    getLanguageHeaders(language)
  );

export const getParameterGroupLookup = (language?: string) =>
  ApiService.get<LookupResponse>(
    ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.PARAMETER_GROUP,
    getLanguageHeaders(language)
  );

export const getParametersList = (params: SetupListParams = {}, language?: string) =>
  ApiService.get<{ response: SetupPaginatedResponse<ParameterRecord> }>(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETERS.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    }
  );

export const createParameter = (payload: ParameterPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETERS.ADD,
    payload,
    getLanguageHeaders(language)
  );

export const updateParameter = (
  id: string | number,
  payload: Partial<ParameterPayload> | StatusPayload,
  language?: string
) =>
  ApiService.patch(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETERS.UPDATE, id),
    payload,
    getLanguageHeaders(language)
  );

export const deleteParameter = (id: string | number, language?: string) =>
  ApiService.delete(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETERS.DELETE, id),
    getLanguageHeaders(language)
  );

export const getParameterColumns = (language?: string) =>
  ApiService.get(
    ServicesUrlEndpoints.ADMINISTRATION.SETUP.PARAMETERS.COLUMN,
    getLanguageHeaders(language)
  );

export const getParameterLookup = (language?: string) =>
  ApiService.get<LookupResponse>(
    ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.PARAMETERS,
    getLanguageHeaders(language)
  );

export const getParametersByMakeId = (id: string | number, language?: string) =>
  ApiService.get<LookupResponse>(
    replaceId(ServicesUrlEndpoints.ADMINISTRATION.LOOKUP.PROPERTY_BY_MAKE_ID, id),
    getLanguageHeaders(language)
  );

export type SetupMutationResponse = AxiosResponse<unknown>;
